import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { MessageBusService } from '../message-bus.service';
import { PanelBaseComponent } from '../panels/panel-base.component';
import { TimestampsService } from '../timestamps.service';
import {UserSettingsService} from "../user-settings.service";
import {ColGroupDef, GridOptions, GridReadyEvent} from "ag-grid-community";
import {DetectMethodChanges} from "../utils";
import {getGridOptions, TestPanelRowData} from "./hm-columns-layout/grid-options";

@Component({
   selector: 'ets-test-panel',
   templateUrl: 'test-panel.component.html',
   styleUrls: ['test-panel.component.scss'],
   changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestPanelComponent extends PanelBaseComponent {

   constructor(
      protected readonly _changeDetector: ChangeDetectorRef,
      protected readonly _userSettingsService: UserSettingsService,
      protected readonly _messageBus: MessageBusService,
      private readonly _timestampsService: TimestampsService
   ) {
      super(_changeDetector, _userSettingsService, _messageBus);
   }

   gridOptions: GridOptions;
   gridApi: GridReadyEvent;
   private _hedgeCount = 15;

   ngOnInit() {
      this.gridOptions = getGridOptions.bind(this)();
   }

   //#region base interface

   @DetectMethodChanges()
   etsOnInit(): void {}

   etsOnDestroy(): void {}

   etsAfterViewInit(): void {
      this._changeDetector.detach();
   }

   protected getState() { return null; }
   protected setState(state: any) {}

   //#endregion

   @DetectMethodChanges()
   addHedgeViaSetColumnDefs(insertBeforeColId?: string) {
      this._hedgeCount++;
      const hedgeName = `Hedge ${this._hedgeCount}`;
      const field = `hedge${this._hedgeCount}`;

      const newGroup: ColGroupDef = {
         headerName: hedgeName,
         marryChildren: true,
         groupId: `${field}:group`,
         children: [
            {
               headerName: hedgeName,
               colId: `${field}:qty`,
               field: field,
            },
            {
               headerName: 'Trans',
               colId: `${field}:trans`,
               field: `${field}-trans`,
               hide: true,
            },
            {
               headerName: 'Outcome',
               colId: `${field}:outcome`,
               field: `${field}-outcome`,
               hide: true,
            }
         ]
      };

      // Save scroll position
      const scrollState = this.gridOptions.columnApi.getColumnState();

      // Get current defs, sanitize, append new group before Total
      const currentDefs = this.gridOptions.api.getColumnDefs();
      const sanitized = this.sanitizeColumnDefs(currentDefs);

      // Insert before Total column (last in array)
      sanitized.splice(sanitized.length - 1, 0, newGroup);

      this.gridOptions.api.setColumnDefs(sanitized);

      // Restore column state (order/visibility/width) + position new column
      if (insertBeforeColId) {
         const state = this.gridOptions.columnApi.getColumnState();
         const newQtyColId = `${field}:qty`;
         const filtered = state.filter(c => c.colId !== newQtyColId);
         const entry = state.find(c => c.colId === newQtyColId);
         if (entry) {
            const targetIdx = filtered.findIndex(c => c.colId === insertBeforeColId);
            if (targetIdx >= 0) {
               filtered.splice(targetIdx, 0, entry);
            } else {
               filtered.push(entry);
            }
            this.gridOptions.columnApi.setColumnState(filtered);
         }
      }

      console.log(`Added ${hedgeName} — total hedges: ${this._hedgeCount}`);
   }

   private sanitizeColumnDefs(colDefs: any[]): any[] {
      colDefs.forEach(colDef => {
         Object.keys(colDef).forEach(key => {
            if (colDef[key] === null) {
               delete colDef[key];
            }
         });
         if (colDef.children && Array.isArray(colDef.children)) {
            this.sanitizeColumnDefs(colDef.children);
         }
      });
      return colDefs;
   }

   makeRows() {
      const rows: TestPanelRowData[] = [];
      for (let i = 0; i < 40; i++) {
         const strike = 6000 + i * 5;
         const row: TestPanelRowData = { strike };
         // Generate data for all 15 initial hedges
         for (let h = 1; h <= 15; h++) {
            row[`hedge${h}`] = parseFloat((Math.random() * 100 - 50).toFixed(1));
            row[`hedge${h}-trans`] = parseFloat((Math.random() * 100 - 50).toFixed(1));
            row[`hedge${h}-outcome`] = parseFloat((Math.random() * 100 - 50).toFixed(1));
         }
         row.total = parseFloat((Math.random() * 10000 - 5000).toFixed(1));
         rows.push(row);
      }
      return rows;
   }

   @DetectMethodChanges()
   onGridReady(event: GridReadyEvent) {
      this.gridApi = event;
      const rows = this.makeRows();
      this.gridApi.api.setRowData(rows);
   }
}
